import React, { useState, useEffect, useRef } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

const TYPE_GRADIENTS = {
  "Garden Project": "linear-gradient(135deg, #7A9E7E 0%, #4E7A52 100%)",
  "House Project": "linear-gradient(135deg, #C9963A 0%, #A07820 100%)",
  "Fish Tank Project": "linear-gradient(135deg, #6B9EAD 0%, #4A7A87 100%)",
  "Personal Project": "linear-gradient(135deg, #8A93CC 0%, #5A6AB0 100%)",
  "Organization & Cleaning": "linear-gradient(135deg, #C97B7B 0%, #A05050 100%)",
};
const DEFAULT_GRADIENT = "linear-gradient(135deg, #B0AEA9 0%, #888480 100%)";
const TYPE_OPTIONS = ["Garden Project", "House Project", "Fish Tank Project", "Personal Project", "Organization & Cleaning"];
const STATUS_OPTIONS = ["Future", "Planned", "Getting Estimates", "In Progress", "Ongoing", "Completed"];
const LOCATION_OPTIONS = ["Inside", "Outside"];

function formatDate(d) {
  if (!d) return null;
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function StatusBadge({ status, light }) {
  const colorMap = {
    Future: { bg: "#FAF0F0", color: "#A05050" },
    Planned: { bg: "#F2F0ED", color: "#7A6A50" },
    "Getting Estimates": { bg: "#EAF4EA", color: "#3A7A3E" },
    "In Progress": { bg: "#FDF6E3", color: "#A07820" },
    Ongoing: { bg: "#F2F0ED", color: "#9E9B96" },
    Completed: { bg: "#EEF0FA", color: "#5A6AB0" },
  };
  const s = light ? { bg: "rgba(255,255,255,0.25)", color: "#fff" } : (colorMap[status] || colorMap.Planned);
  return <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", padding: "3px 8px", borderRadius: 20, background: s.bg, color: s.color, backdropFilter: light ? "blur(4px)" : "none" }}>{status}</span>;
}

function CloseButton({ onClose }) {
  return (
    <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 14, right: 14, width: 30, height: 30, borderRadius: "50%", background: "var(--cream-hover)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 2 }}>
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1 1L12 12M12 1L1 12" stroke="#9E9B96" strokeWidth="1.8" strokeLinecap="round" /></svg>
    </button>
  );
}

function Sheet({ onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} />
      <div style={{ position: "relative", background: "var(--white)", borderRadius: "20px 20px 0 0", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 0" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <CloseButton onClose={onClose} />
        {children}
        <div style={{ height: 32 }} />
      </div>
    </div>
  );
}

function EditSheetShell({ title, onClose, onSave, saving, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
      <div style={{ position: "relative", background: "var(--white)", borderRadius: "20px 20px 0 0", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)" }}>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)" }}>{title}</span>
          <button onClick={onSave} disabled={saving} style={{ background: "none", border: "none", fontSize: 14, fontWeight: 800, color: saving ? "var(--muted)" : "var(--sage)", cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>{saving ? "Saving…" : "Save"}</button>
        </div>
        <div style={{ overflowY: "auto", paddingBottom: 24 }}>{children}</div>
      </div>
    </div>
  );
}

const field = { padding: "12px 20px", borderBottom: "1px solid var(--border)" };
const lbl = { fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 };
const textInputStyle = { width: "100%", border: "none", outline: "none", fontSize: 15, fontWeight: 500, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" };
const selStyle = { border: "none", outline: "none", fontSize: 14, fontWeight: 600, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent", cursor: "pointer" };

function MultiSelectDropdown({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isFiltered = selected.length > 0;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (opt) => onChange(selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt]);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: `1.5px solid ${isFiltered ? "var(--charcoal)" : "var(--border)"}`, background: isFiltered ? "var(--charcoal)" : "var(--white)", color: isFiltered ? "#fff" : "var(--charcoal)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
        {label}
        {isFiltered && <span style={{ background: "#fff", color: "var(--charcoal)", borderRadius: 10, fontSize: 11, fontWeight: 700, padding: "1px 6px" }}>{selected.length}</span>}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, background: "var(--white)", borderRadius: 12, border: "1px solid var(--border)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 200, overflow: "hidden" }}>
          {options.map((opt, i) => {
            const checked = selected.includes(opt);
            return (
              <div key={opt} onClick={() => toggle(opt)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 16px", cursor: "pointer", borderBottom: i < options.length - 1 ? "1px solid var(--border)" : "none", background: checked ? "var(--cream-hover)" : "transparent" }}>
                <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: "var(--charcoal)" }}>{opt}</span>
                <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, border: checked ? "none" : "2px solid var(--border)", background: checked ? "var(--sage)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {checked && <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>✓</span>}
                </div>
              </div>
            );
          })}
          <div style={{ display: "flex", borderTop: "1px solid var(--border)" }}>
            <button onClick={() => { onChange(options); setOpen(false); }} style={{ flex: 1, padding: "9px 0", fontSize: 12, color: "var(--sage)", fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit" }}>Select all</button>
            <button onClick={() => { onChange([]); setOpen(false); }} style={{ flex: 1, padding: "9px 0", fontSize: 12, color: "var(--muted)", fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit" }}>Clear</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectEditSheet({ project, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: project.name || "", status: project.status || "Future", type: project.type || "",
    location: project.location || [], details: project.details || "", estimates: project.estimates || "",
    plannedDate: project.plannedDate || "", dueDate: project.dueDate || "",
  });
  const [saving, setSaving] = useState(false);

  const toggleLocation = (l) => setForm(f => ({ ...f, location: f.location.includes(l) ? f.location.filter(x => x !== l) : [...f.location, l] }));

  const handleSave = async () => {
    setSaving(true);
    await fetch("/.netlify/functions/notion", {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: project.id, action: "updateProject", value: form }),
    });
    setSaving(false);
    onSaved({ ...project, ...form });
  };

  return (
    <EditSheetShell title="Edit Project" onClose={onClose} onSave={handleSave} saving={saving}>
      <div style={field}><div style={lbl}>Name</div><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={textInputStyle} /></div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={lbl}>Status</div>
        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={selStyle}>{STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}</select>
      </div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={lbl}>Type</div>
        <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={selStyle}>
          <option value="">None</option>
          {TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div style={field}>
        <div style={lbl}>Location</div>
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {LOCATION_OPTIONS.map(l => (
            <button key={l} onClick={() => toggleLocation(l)} style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${form.location.includes(l) ? "var(--sage)" : "var(--border)"}`, background: form.location.includes(l) ? "var(--sage)" : "transparent", color: form.location.includes(l) ? "#fff" : "var(--muted)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={lbl}>Planned Date</div>
        <input type="date" value={form.plannedDate} onChange={e => setForm({ ...form, plannedDate: e.target.value })} style={{ ...textInputStyle, width: "auto" }} />
      </div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={lbl}>Due Date</div>
        <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} style={{ ...textInputStyle, width: "auto" }} />
      </div>
      <div style={field}><div style={lbl}>Details</div><textarea rows={3} value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} style={{ ...textInputStyle, resize: "none", lineHeight: 1.5 }} /></div>
      <div style={{ ...field, borderBottom: "none" }}><div style={lbl}>Estimates</div><textarea rows={2} value={form.estimates} onChange={e => setForm({ ...form, estimates: e.target.value })} style={{ ...textInputStyle, resize: "none", lineHeight: 1.5 }} /></div>
    </EditSheetShell>
  );
}

function ProjectCard({ project, onTap }) {
  const gradient = TYPE_GRADIENTS[project.type] || DEFAULT_GRADIENT;
  const isCompleted = project.status === "Completed";
  return (
    <div onClick={() => onTap(project)} style={{ background: "var(--white)", borderRadius: 16, marginBottom: 12, border: "1px solid var(--border)", overflow: "hidden", cursor: "pointer", opacity: isCompleted ? 0.75 : 1, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      <div style={{ height: 100, position: "relative", background: project.coverPhoto ? `url(${project.coverPhoto}) center/cover no-repeat` : gradient }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.5) 100%)" }} />
        <div style={{ position: "absolute", bottom: 10, left: 14, right: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>{project.name}</div>
          <StatusBadge status={project.status} light />
        </div>
      </div>
      <div style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>{project.type || <span style={{ fontStyle: "italic" }}>No type</span>}</div>
        {project.location?.length > 0 && <div style={{ fontSize: 11, color: "var(--muted)" }}>{project.location.join(" · ")}</div>}
      </div>
    </div>
  );
}

function DetailRow({ label, value, empty = "Not set" }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: value ? "var(--charcoal)" : "var(--muted)", fontStyle: value ? "normal" : "italic", lineHeight: 1.5 }}>{value || empty}</div>
    </div>
  );
}

function ProjectDetail({ project: initialProject, onClose, readOnly = false }) {
  const [project, setProject] = useState(initialProject);
  const [showEdit, setShowEdit] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const gradient = TYPE_GRADIENTS[project.type] || DEFAULT_GRADIENT;

  useEffect(() => {
    fetch(`/.netlify/functions/notion?type=projectDetail&pageId=${project.id}`)
      .then(r => r.json()).then(d => setBlocks(d.blocks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  return (
    <>
      <Sheet onClose={onClose}>
        {/* Hero banner */}
        <div style={{ height: 150, position: "relative", margin: "8px 0 0", background: project.coverPhoto ? `url(${project.coverPhoto}) center/cover no-repeat` : gradient }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.55) 100%)" }} />
          <div style={{ position: "absolute", bottom: 14, left: 20, right: 20 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,0.3)", marginBottom: 6 }}>{project.name}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <StatusBadge status={project.status} light />
              {project.type && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{project.type}</span>}
              {project.location?.length > 0 && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{project.location.join(" · ")}</span>}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        {!readOnly && (
          <div style={{ padding: "14px 20px 16px", display: "flex", gap: 8 }}>
            <button onClick={() => setShowEdit(true)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "1.5px solid var(--sage)", color: "var(--sage)", fontWeight: 700, fontSize: 13, background: "var(--white)", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
            <a href={project.notionUrl} target="_blank" rel="noreferrer" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--border)", color: "var(--muted)", fontWeight: 600, fontSize: 13, textDecoration: "none", background: "var(--cream-hover)" }}>Edit in Notion ↗</a>
          </div>
        )}

        {/* All fields — always shown */}
        <div style={{ margin: "0 20px 16px", padding: "0 14px", background: "var(--cream-hover)", borderRadius: 12 }}>
          <DetailRow label="Planned Date" value={project.plannedDate ? formatDate(project.plannedDate) : null} />
          <DetailRow label="Due Date" value={project.dueDate ? formatDate(project.dueDate) : null} />
          <DetailRow label="Details" value={project.details} />
          <DetailRow label="Estimates" value={project.estimates} empty="No estimates yet" />
        </div>

        {/* Notion page content / notes */}
        {loading && <div style={{ padding: "0 20px 16px", color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>Loading notes…</div>}
        {!loading && blocks.length > 0 && (
          <div style={{ margin: "0 20px 16px" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>Notes</div>
            <div style={{ padding: "12px 14px", background: "var(--cream-hover)", borderRadius: 10, fontSize: 13, color: "var(--charcoal)", lineHeight: 1.6 }}>
              {blocks.map((b, i) => <div key={i} style={{ marginBottom: i < blocks.length - 1 ? 6 : 0 }}>{b.type === "bulleted_list_item" ? `• ${b.text}` : b.text}</div>)}
            </div>
          </div>
        )}
        {!loading && blocks.length === 0 && (
          <div style={{ margin: "0 20px 16px", padding: "12px 14px", background: "var(--cream-hover)", borderRadius: 10, fontSize: 13, color: "var(--muted)", fontStyle: "italic", textAlign: "center" }}>
            No notes yet. <a href={project.notionUrl} target="_blank" rel="noreferrer" style={{ color: "var(--sage)", textDecoration: "none", fontWeight: 700 }}>Add in Notion ↗</a>
          </div>
        )}
      </Sheet>
      {showEdit && <ProjectEditSheet project={project} onClose={() => setShowEdit(false)} onSaved={(updated) => { setProject(updated); setShowEdit(false); }} />}
    </>
  );
}

function AddProjectSheet({ onClose, onAdd }) {
  const [form, setForm] = useState({ name: "", status: "Future", type: "", location: [], details: "", estimates: "", plannedDate: "", dueDate: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleLocation = (l) => setForm(f => ({ ...f, location: f.location.includes(l) ? f.location.filter(x => x !== l) : [...f.location, l] }));

  const handleCreate = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch("/.netlify/functions/notion", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "createProject", value: { ...form, name: form.name.trim() } }),
      });
      const data = await res.json();
      if (data.item) { onAdd(data.item); onClose(); }
      else setError("Failed to create project");
    } catch { setError("Something went wrong"); }
    finally { setSaving(false); }
  };

  return (
    <EditSheetShell title="New Project" onClose={onClose} onSave={handleCreate} saving={saving}>
      {error && <div style={{ padding: "8px 20px", color: "var(--rose)", fontSize: 13, fontWeight: 600 }}>{error}</div>}
      <div style={field}><div style={lbl}>Name *</div><input autoFocus value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={textInputStyle} /></div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={lbl}>Status</div>
        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={selStyle}>{STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}</select>
      </div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={lbl}>Type</div>
        <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={selStyle}>
          <option value="">None</option>
          {TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div style={field}>
        <div style={lbl}>Location</div>
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {LOCATION_OPTIONS.map(l => (
            <button key={l} onClick={() => toggleLocation(l)} style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${form.location.includes(l) ? "var(--sage)" : "var(--border)"}`, background: form.location.includes(l) ? "var(--sage)" : "transparent", color: form.location.includes(l) ? "#fff" : "var(--muted)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ ...field, borderBottom: "none" }}><div style={lbl}>Details</div><textarea rows={3} value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} style={{ ...textInputStyle, resize: "none", lineHeight: 1.5 }} /></div>
    </EditSheetShell>
  );
}

export default function FamilyProjectsPage({ onBack, onTabTap, readOnly = false }) {
  const [tab, setTab] = useState("active");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [typeFilter, setTypeFilter] = useState([]);
  const [locationFilter, setLocationFilter] = useState([]);

  useEffect(() => {
    setLoading(true); setError(null);
    fetch(`/.netlify/functions/notion?type=projects&completed=${tab === "completed"}`)
      .then(r => r.json())
      .then(d => { if (d.error) { setError(JSON.stringify(d.error)); setProjects([]); } else setProjects(d.items || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [tab]);

  const filtered = projects.filter(p => {
    if (typeFilter.length > 0 && !typeFilter.includes(p.type)) return false;
    if (locationFilter.length > 0 && !locationFilter.some(l => p.location?.includes(l))) return false;
    return true;
  });

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", padding: "54px 20px 0", borderBottom: "2px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>💾 Family Projects</span>
            </div>
            {!readOnly && <button onClick={() => setShowAdd(true)} style={{ padding: "8px 16px", borderRadius: 10, background: "var(--sage)", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>}
          </div>
          <div style={{ display: "flex" }}>
            {[{ id: "active", label: "Active" }, { id: "completed", label: "Completed" }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, textAlign: "center", padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit", borderBottom: `2.5px solid ${tab === t.id ? "var(--sage)" : "transparent"}`, color: tab === t.id ? "var(--sage)" : "var(--muted)" }}>{t.label}</button>
            ))}
          </div>
        </header>

        <div className="scroll-body" style={{ padding: "12px 16px" }}>
          {/* Filters */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <MultiSelectDropdown label="Type" options={TYPE_OPTIONS} selected={typeFilter} onChange={setTypeFilter} />
            <MultiSelectDropdown label="Location" options={LOCATION_OPTIONS} selected={locationFilter} onChange={setLocationFilter} />
          </div>

          {loading && <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div>}
          {error && <div style={{ padding: 20, color: "var(--rose)", fontSize: 13, background: "var(--cream-hover)", borderRadius: 10, marginBottom: 12 }}>Error: {error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "var(--muted)", fontSize: 14 }}>
              {projects.length === 0
                ? (tab === "completed" ? "No completed projects yet." : "No active projects yet.")
                : "No projects match this filter."}
            </div>
          )}
          {!loading && filtered.map(p => <ProjectCard key={p.id} project={p} onTap={setSelectedProject} />)}
        </div>

        <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
            </div>
          ))}
        </nav>
      </div>
      {selectedProject && <ProjectDetail project={selectedProject} onClose={() => setSelectedProject(null)} readOnly={readOnly} />}
      {showAdd && <AddProjectSheet onClose={() => setShowAdd(false)} onAdd={(p) => setProjects(prev => [p, ...prev])} />}
    </>
  );
}
